// SeaFrame — the render side of the sea axis (docs/sea-design.md A2).
//
// The design's sim-side `SeaFrame { ocean_rings, coast_rings, wall_rings }`
// classifies tiles by `edge_distance`. The frame itself is the wire shape
// the sim publishes (`state.globals.sea`), so the renderer reads the real
// rings instead of guessing them from the tiles. Rim = {1, 1, 0} is today's
// board.

import type { Sea, SeaFrame } from '@aod/protocol';

export type { SeaFrame };

/** The sea modes the renderer knows (the sim's four, minus none). */
export type SeaMode = Sea;

/** Today's board: one ocean ring, one coast ring, no wall. */
export const RIM_FRAME: SeaFrame = { oceanRings: 1, coastRings: 1, wallRings: 0 };
/** Landlocked: the ocean + coast band becomes a 2-thick mountain wall. */
export const LANDLOCKED_FRAME: SeaFrame = { oceanRings: 0, coastRings: 0, wallRings: 2 };
/** Design default for the extra ocean width of `open`. */
export const OPEN_SEA_WIDTH = 3;
export const OPEN_SEA_MIN_WIDTH = 1;
export const OPEN_SEA_MAX_WIDTH = 5;

/** Open sea: Rim land plus `width` extra ocean rings (design: 1+W ocean, 1 coast). */
export function openFrame(width: number = OPEN_SEA_WIDTH): SeaFrame {
  const w = Math.min(OPEN_SEA_MAX_WIDTH, Math.max(OPEN_SEA_MIN_WIDTH, Math.round(width)));
  return { oceanRings: 1 + w, coastRings: 1, wallRings: 0 };
}

export function frameForMode(mode: SeaMode, width: number = OPEN_SEA_WIDTH): SeaFrame {
  if (mode === 'landlocked') return LANDLOCKED_FRAME;
  if (mode === 'open') return openFrame(width);
  return RIM_FRAME;
}

/**
 * Ocean rings to ADD outside a board that already follows `base` (the real
 * board is Rim today, so its outer ocean ring exists already).
 */
export function extraOceanRings(frame: SeaFrame, base: SeaFrame = RIM_FRAME): number {
  return Math.max(0, frame.oceanRings - base.oceanRings);
}

/** True when the frame has any water at all (ships only exist where there is sea). */
export function frameHasSea(frame: SeaFrame): boolean {
  return frame.oceanRings + frame.coastRings > 0;
}

/**
 * Pinned terrain level of a wall row (0 = outermost): row 0 = 12, row 1 = 11,
 * deeper rows (not used by the design default) step down. Rows are excluded
 * from the sim's level offers, so these never feed the interior.
 */
export function wallLevel(row: number): number {
  return Math.max(0, 12 - Math.max(0, row));
}

/** Mode named by a `?seaProto=` query value, or null (default OFF). */
export function parseSeaProto(search: string): SeaMode | null {
  const v = new URLSearchParams(search).get('seaProto');
  return v === 'open' || v === 'landlocked' || v === 'rim' ? v : null;
}

/** Extra-ocean width from `?seaWidth=` (clamped), else the design default. */
export function parseSeaWidth(search: string): number {
  const n = Number(new URLSearchParams(search).get('seaWidth'));
  return Number.isFinite(n) && n > 0 ? openFrame(n).oceanRings - 1 : OPEN_SEA_WIDTH;
}
